/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { RangeSlider, VisualOnlyLabel } from 'sarsaparilla';

import {
    getUiFilterSelections,
    type UiFilterOption,
    type UiFiltersState,
} from '../../../utils/filters';

import styles from './TourDurationFilters.module.scss';

interface TourDurationFiltersProps {
    uiFilters: UiFiltersState;
    onFilterChange: (filterName: string, option: UiFilterOption) => void;
    onFilterRemove: (filterName: string, option: UiFilterOption) => void;
}
type Range = number[];

const MIN_VALUE = 0;
const MAX_VALUE = 8;

/**
 * Tour duration range slider for the `tourDuration` UI filter
 */
export function TourDurationFilters({
    uiFilters,
    onFilterChange,
    onFilterRemove,
}: TourDurationFiltersProps) {
    const tourDurationFilter = getUiFilterSelections(uiFilters, 'tourDuration')[
        'tour-duration'
    ]?.filters?.[0]?.filterValue;

    const keyRef = React.useRef(0);
    // settledValue is both the RangeSlider default value and a means of tracking the value
    // in comparison to the filter value so we can sync a "clear all filters" action.
    // It is NOT used to control RangeSlider
    const [settledValue, setSettledValue] = React.useState<Range>(() => {
        return tourDurationFilter
            ? reduxStringToRangeValue(tourDurationFilter)
            : [MIN_VALUE, MAX_VALUE];
    });

    // If the filters are cleared externally, we reset the slider state using a key change
    // Otherwise we assume that the RangeSlider state and filter value match
    // since this is the only control for that value
    React.useEffect(() => {
        if (!tourDurationFilter && !doesValueMatchInitial(settledValue)) {
            keyRef.current += 1;
            setSettledValue([MIN_VALUE, MAX_VALUE]);
        }
    }, [tourDurationFilter, settledValue]);

    // When the user stops moving the slider, we update the filter
    const onChangeEnd = ([min, max]: Range) => {
        setSettledValue([min, max]);
        const filterOption = {
            key: 'tour-duration',
            filters: [
                {
                    attribute: 'tour_duration',
                    filterValue: rangeValueToReduxString([min, max]),
                },
            ],
        };

        if (doesValueMatchInitial([min, max])) {
            // If the value matches the initial value, we remove the filter
            // Lord only knows why you have to pass `filterOption` but apparently you do
            onFilterRemove('tourDuration', filterOption);
        } else {
            onFilterChange('tourDuration', filterOption);
        }
    };

    return (
        <div className={styles.tourDurationFilters}>
            {/* Visual only because RangeSlider provides its own accessibility labels */}
            <VisualOnlyLabel className={styles.label}>Tour Duration</VisualOnlyLabel>
            <RangeSlider
                key={keyRef.current}
                className={styles.slider}
                label="Tour Duration"
                isLabelVisible={false}
                defaultValue={settledValue}
                onChangeEnd={onChangeEnd}
                minValue={MIN_VALUE}
                maxValue={MAX_VALUE}
                valueLabelPosition="thumbs"
                formatOptions={{
                    style: 'unit',
                    unit: 'hour',
                }}
            />
        </div>
    );
}

/**
 * Converts the string format stored in redux (">=0 240") to range value ([0, 4])
 */
function reduxStringToRangeValue(input: string): Range {
    // Remove any comparison operators
    const cleaned = input.replace(/^[<>]=?/, '').trim();
    // Split by space and convert each part to a number, divided by 60 to gets hours
    const split = cleaned.split(' ');

    if (split.length !== 2) {
        return [MIN_VALUE, MAX_VALUE];
    }

    return split.map((item) => {
        return Number(item) / 60;
    }) as Range;
}

/**
 * Converts a range value ([0, 4]) to the redux string (">=0 240")
 */
function rangeValueToReduxString(range: Range): string {
    const [min, max] = range;
    const minHours = min * 60;
    const maxHours = max * 60;
    return `>=${minHours} ${maxHours}`;
}

/**
 * Compare a range value to the default/initial value
 */
function doesValueMatchInitial([min, max]: Range): boolean {
    return min === MIN_VALUE && max === MAX_VALUE;
}
